import {
  AddIcon,
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  EmptyState,
  Grid,
  MenuItem,
  SelectField,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@all-manager/design-system-v2';
import { Line } from 'react-chartjs-2';
import { ExpenseCategoryDatabase, ExpenseDatabase } from '../../../../types';
import { formatExpenseMonth } from '../../utils/expenseStats';
import { formatAmount } from '../../utils/formatAmount';
import CategoryYearTotals from '../CategoryYearTotals/CategoryYearTotals';
import ExpenseFormDialog from '../ExpenseFormDialog/ExpenseFormDialog';
import ExpenseListDialog from '../ExpenseListDialog/ExpenseListDialog';
import ExpenseMenuAction from '../ExpenseMenuAction/ExpenseMenuAction';
import { useCategoryDetails } from './useCategoryDetails';

type Props = {
  categories: ExpenseCategoryDatabase[];
  expenses: ExpenseDatabase[];
};

export default function CategoryDetails(props: Props) {
  const hook = useCategoryDetails(props);
  const categoryName = hook.selectedCategory?.name ?? '';

  return (
    <Stack spacing={2}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }} justifyContent="space-between">
        <SelectField<string>
          label="Catégorie"
          value={hook.selectedCategoryId}
          onChange={(event) => hook.handleCategoryChange(event.target.value)}
        >
          {props.categories.map((category) => (
            <MenuItem key={category.id} value={category.id}>{category.name}</MenuItem>
          ))}
        </SelectField>

        {hook.isAdmin ? (
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon />}
            onClick={hook.handleOpenExpenseForm}
          >
            Nouvelle dépense
          </Button>
        ) : null}
      </Stack>

      {hook.categoryExpenses.length === 0 ? (
        <Card>
          <EmptyState
            title={`Aucune dépense dans « ${categoryName} »`}
            subtitle={hook.isAdmin
              ? 'Ajoutez une première dépense pour suivre son évolution mois par mois.'
              : 'Les dépenses de cette catégorie apparaîtront ici dès qu\'elles seront saisies.'}
          />
        </Card>
      ) : (
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 8 }}>
            <Card>
              <CardHeader title="Évolution mensuelle" subheader="Une courbe par année" />
              <CardContent>
                <Line
                  data={hook.chartData}
                  options={hook.chartOptions}
                  aria-label={`Dépenses mensuelles en ${categoryName}, totaux annuels dans le tableau`}
                  role="img"
                />
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, md: 4 }}>
            <Stack spacing={2}>
              <Card>
                <CardHeader title="Totaux annuels" />
                <CategoryYearTotals aria-label={`Totaux annuels en ${categoryName}`} totals={hook.yearTotals} />
              </Card>

              {hook.isAdmin ? (
                <Card>
                  <CardHeader title="Dernières dépenses" />
                  <Table size="small" aria-label={`Dernières dépenses en ${categoryName}`}>
                    <TableHead>
                      <TableRow>
                        <TableCell>Mois</TableCell>
                        <TableCell align="right">Montant</TableCell>
                        <TableCell padding="checkbox" />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {hook.latestExpenses.map((expense) => (
                        <TableRow key={expense.id}>
                          <TableCell>{formatExpenseMonth(expense.month)}</TableCell>
                          <TableCell align="right">
                            <Typography variant="body1Emphasis">{formatAmount(expense.amount)}</Typography>
                          </TableCell>
                          <TableCell padding="checkbox">
                            <ExpenseMenuAction expense={expense} categories={props.categories} />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  {hook.hasMoreExpenses ? (
                    <CardActions>
                      <Button variant="text" size="small" color="default" onClick={hook.handleOpenExpenseList}>
                        Voir toutes les dépenses
                      </Button>
                    </CardActions>
                  ) : null}
                </Card>
              ) : null}
            </Stack>
          </Grid>
        </Grid>
      )}

      {hook.isExpenseFormOpen ? (
        <ExpenseFormDialog
          categories={props.categories}
          defaultCategoryId={hook.selectedCategoryId}
          onClose={hook.handleCloseExpenseForm}
        />
      ) : null}

      {hook.isExpenseListOpen ? (
        <ExpenseListDialog
          title={`Dépenses ${categoryName}`}
          expenses={hook.allExpensesLatestFirst}
          categories={props.categories}
          onClose={hook.handleCloseExpenseList}
        />
      ) : null}
    </Stack>
  );
}
